/**
 * Поддержка браузера — soft-слой.
 *
 * Hard-граница (браузер вообще не запускает основной бандл) ловится ES5-детектором
 * в `index.html` Front, который уводит на статичную `public/unsupported-browser.html`.
 * Здесь — «мягкая» проверка: браузер работает, но устарел относительно рекомендуемой
 * планки; для него показываем ненавязчивый баннер с предложением обновиться
 * (`BrowserSupportBanner` во Front).
 *
 * Маркеры выведены из реальных зависимостей кода (аудит CSS/JS-фич), а не из
 * абстрактной планки: hard-граница — CSS `@layer` (см. `index.html`), soft-граница —
 * CSS `:has()` (ниже). При заметной смене стека/фич — пересмотреть оба детектора.
 */

/** Ключ localStorage: timestamp, до которого баннер не показываем. */
const DISMISS_KEY = 'bidzaar.browserOutdated.dismissedUntil';
/** Через сколько дней после «Позже» напомнить снова. */
const DISMISS_DAYS = 14;

/** Безопасное чтение localStorage (приватный режим Safari и т. п.). */
const safeGet = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

/** Безопасная запись localStorage. */
const safeSet = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // приватный режим / переполнение — молча игнорируем
    }
};

/**
 * Браузер запускается (hard-границу прошёл), но устарел: не поддерживает `:has()`.
 * Это самая «свежая» из реально используемых фич — раскраска статусов в гридах,
 * тени и отступы (≈28 файлов стилей). Без неё приложение работает, но с косметической
 * деградацией, поэтому показываем ненавязчивый баннер, а не уводим со страницы.
 * Граница `:has()`: Chrome 105 / Firefox 121 / Safari 15.4.
 */
export const isBrowserOutdated = (): boolean => {
    try {
        return typeof CSS === 'undefined' || typeof CSS.supports !== 'function' || !CSS.supports('selector(:has(*))');
    } catch {
        // если сама проверка упала — не докучаем пользователю баннером
        return false;
    }
};

/** Прямая ссылка на страницу скачивания браузера, которым пользуется посетитель. */
export const getBrowserUpdateUrl = (): string => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (/Edg\//.test(ua)) return 'https://www.microsoft.com/edge';
    if (/YaBrowser/.test(ua)) return 'https://browser.yandex.ru/';
    if (/Firefox\//.test(ua)) return 'https://www.mozilla.org/firefox/new/';
    if (/OPR\/|Opera/.test(ua)) return 'https://www.opera.com/';
    if (/Chrome\//.test(ua)) return 'https://www.google.com/chrome/';
    if (/Safari\//.test(ua)) return 'https://www.apple.com/safari/';
    return 'https://www.google.com/chrome/';
};

/** Баннер скрыт пользователем и срок «не напоминать» ещё не истёк. */
export const isUpdateDismissed = (): boolean => {
    const raw = safeGet(DISMISS_KEY);
    if (!raw) return false;
    const until = Number(raw);
    return Number.isFinite(until) && Date.now() < until;
};

/** Отложить показ баннера на {@link DISMISS_DAYS} дней. */
export const dismissUpdate = (): void => {
    safeSet(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
};
